import cn from 'classnames';
import {
  CSSProperties,
  ReactNode, useEffect, useRef, useState,
} from 'react';

import { useUpdateEffect } from '../../../utils/useUpdateEffect';

export enum TransitionStatus {
  ENTERED = 'entered',
  EXITED = 'exited',
  ENTERING = 'entering',
  EXITING = 'exiting',
  UNMOUNTED = 'unmounted',
}

type TTransitionProps<T extends React.ElementType> = React.ComponentProps<T> & {
  in: boolean;
  as?: T
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  /** if true then entering transition will be also be played on mounting */
  appear?: boolean;
  /**
   * duration of transition between states, this value is accessible in css\
   * via variable `--transition-duration`
   */
  duration?: number;
  /** defines if children of component should be rendered while `in` is false */
  keepMounted?: boolean;
  /**
   * classnames to override transition classnames, you can provide either all or none
   */
  classNames?: {
    entering?: string;
    entered?: string;
    exiting?: string;
    exited?: string;
  },
  /**
   * styles to override transtion styles, you can provide either all or none,\
   */
  styles?: {
    entering?: CSSProperties;
    entered?: CSSProperties;
    exiting?: CSSProperties;
    exited?: CSSProperties;
  }
}

/**
 * transition component, in css transition duration is accessible via css variable\
 * `--transition-duration` children must be Wrapper, any other children will be ignored
 */
export function Transition<T extends React.ElementType = 'div'>({
  in: isOpen,
  as: Component = 'div',
  children,
  appear = true,
  keepMounted = false,
  duration = 200,
  className,
  style,
  classNames,
  styles,
  ...props
}: TTransitionProps<T>) {
  const childrenRef = useRef<ReactNode>(null);
  const transitionTimeoutIdRef = useRef(0);

  const [transitionStatus, setTransitionStatus] = useState<TransitionStatus>(
    () => {
      if (!keepMounted && !isOpen) {
        return TransitionStatus.UNMOUNTED;
      }
      if (appear || !isOpen) {
        return TransitionStatus.EXITED;
      }
      return TransitionStatus.ENTERED;
    },
  );

  const handleEntering = () => {
    clearTimeout(transitionTimeoutIdRef.current);
    if (transitionStatus === TransitionStatus.UNMOUNTED) {
      setTransitionStatus(TransitionStatus.EXITED);
      setTimeout(() => {
        setTransitionStatus(TransitionStatus.ENTERING);
        transitionTimeoutIdRef.current = window.setTimeout(() => {
          setTransitionStatus(TransitionStatus.ENTERED);
        }, duration);
      });
      return;
    }
    setTransitionStatus(TransitionStatus.ENTERING);
    transitionTimeoutIdRef.current = window.setTimeout(() => {
      setTransitionStatus(TransitionStatus.ENTERED);
    }, duration);
  };

  const handleExiting = () => {
    clearTimeout(transitionTimeoutIdRef.current);
    setTransitionStatus(TransitionStatus.EXITING);
    transitionTimeoutIdRef.current = window.setTimeout(() => {
      setTransitionStatus(keepMounted ? TransitionStatus.EXITED : TransitionStatus.UNMOUNTED);
    }, duration);
  };

  // handle trigger transition
  useUpdateEffect(() => {
    if (isOpen) {
      handleEntering();
    } else {
      handleExiting();
    }
  }, [isOpen]);

  // start appear transition
  useEffect(() => {
    if (appear && isOpen) {
      setTimeout(handleEntering, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // update children only when isOpen is true
  if (isOpen) {
    childrenRef.current = children;
  }

  if (!keepMounted && transitionStatus === TransitionStatus.UNMOUNTED) {
    return null;
  }

  return (
    <Component
      {...props}
      className={cn(
        (classNames && {
          [classNames.entering]: transitionStatus === TransitionStatus.ENTERING,
          [classNames.entered]: transitionStatus === TransitionStatus.ENTERED,
          [classNames.exiting]: transitionStatus === TransitionStatus.EXITING,
          [classNames.exited]: transitionStatus === TransitionStatus.EXITED,
        }),
        className,
      )}
      style={{
        ...style,
        ...({
          [TransitionStatus.ENTERING]: styles?.entering,
          [TransitionStatus.ENTERED]: styles?.entered,
          [TransitionStatus.EXITING]: styles?.exiting,
          [TransitionStatus.EXITED]: styles?.exited,
          [TransitionStatus.UNMOUNTED]: {},
        })[transitionStatus],
        '--transition-duration': `${duration}ms`,
      } as CSSProperties}
    >
      {childrenRef.current}
    </Component>
  );
}
